import { type Map as MapLibre } from 'maplibre-gl';
import { type Color } from '../model/AtlasConfig';
import { type Shape } from '../model/Shape';
import { Vehicle } from '../model/Vehicle';
import { type VehiclePosition } from '../model/VehiclePosition';
import { VehicleType } from '../model/VehicleType';
export class VehicleManager {
  // ---------------------------------------------------------------------
  // Dictionnaire principal : map vehicle.id -> Vehicle
  // ---------------------------------------------------------------------
  private readonly vehicles: Map<string, Vehicle>;

  // Caches pour éviter des conversions répétées (couleurs & types)
  private readonly colorCache: Map<string, Color>;
  private readonly typeCache: Map<string, VehicleType>;
  private visible = true;

  // Instance de la carte MapLibre (peut être undefined si pas encore set)
  private map?: MapLibre;

  constructor() {
    // Initialisation des Maps
    this.vehicles = new Map();
    this.colorCache = new Map();
    this.typeCache = new Map();
  }

  // ---------------------------------------------------------------------
  // Permet de définir la carte sur laquelle on va tracer
  // ---------------------------------------------------------------------
  setMap(map: MapLibre) {
    this.map = map;
  }

  getMap() {
    return this.map;
  }

  // ---------------------------------------------------------------------
  // Ajoute un Vehicle dans la Map de vehicles
  // ---------------------------------------------------------------------
  addVehicle(vehicle: Vehicle) {
    this.vehicles.set(vehicle.id, vehicle);
  }

  // ---------------------------------------------------------------------
  // Retire un Vehicle de la Map
  // ---------------------------------------------------------------------
  removeVehicle(vehicleId: string) {
    this.vehicles.delete(vehicleId);
  }

  // ---------------------------------------------------------------------
  // Retourne un Vehicle spécifique, si présent
  // ---------------------------------------------------------------------
  getVehicle(vehicleId: string): Vehicle | undefined {
    return this.vehicles.get(vehicleId);
  }

  // ---------------------------------------------------------------------
  // Retourne la liste de tous les Vehicles
  // ---------------------------------------------------------------------
  getAllVehicles(): Vehicle[] {
    return Array.from(this.vehicles.values());
  }

  setVisible(visible: boolean) {
    this.visible = visible;
  }

  isVisible() {
    return this.visible;
  }

  // ---------------------------------------------------------------------
  // Convertit une chaîne hexadécimale "rrggbb" en {r, g, b}.
  // Utilise un cache pour éviter la re-conversion à chaque fois.
  // ---------------------------------------------------------------------
  private parseColor(colorStr: string): Color {
    if (colorStr === '' || colorStr === undefined) {
      // Couleur par défaut si la chaîne est vide
      return { r: 255, g: 0, b: 0 };
    }

    // Regarde si on a déjà stocké la couleur dans le cache
    const cached = this.colorCache.get(colorStr);
    if (cached !== undefined) {
      return cached;
    }

    // Calcule la couleur et la stocke
    const color = {
      r: parseInt(colorStr.slice(0, 2), 16),
      g: parseInt(colorStr.slice(2, 4), 16),
      b: parseInt(colorStr.slice(4, 6), 16)
    };
    this.colorCache.set(colorStr, color);
    return color;
  }

  // ---------------------------------------------------------------------
  // Convertit un route_type (ex: "700", "3") en VehicleType (Bus, Tram,...)
  // Utilise un cache interne pour éviter la répétition
  // ---------------------------------------------------------------------
  private getVehicleType(routeType: string): VehicleType {
    const cached = this.typeCache.get(routeType);
    if (cached !== undefined) {
      return cached;
    }

    let type: VehicleType;
    switch (routeType) {
      case '700':
      case '3':
        type = VehicleType.Bus;
        break;
      case '0':
      case '900':
        type = VehicleType.Tram;
        break;
      default:
        type = VehicleType.Bus;
    }

    this.typeCache.set(routeType, type);
    return type;
  }

  smoothOutBearing(shapes: Shape[], windowSize: number = 3): Shape[] {
    if (windowSize < 2) {
      throw new Error('Window size must be at least 2');
    }

    return shapes.map((shape, index) => {
      const neighbors = shapes.slice(Math.max(0, index - windowSize), Math.min(shapes.length, index + windowSize + 1));
      const bearings = neighbors.map(n => n.outBearing);

      // Calculate the average bearing
      const avgBearing = bearings.reduce((sum, bearing) => sum + bearing, 0) / bearings.length;

      return {
        ...shape,
        outBearing: avgBearing
      };
    });
  }

  filterOutliers(shapes: Shape[], windowSize: number = 3, threshold: number = 30): Shape[] {
    if (windowSize < 2) {
      throw new Error('Window size must be at least 2');
    }

    return shapes.filter((shape, index) => {
      const neighbors = shapes.slice(Math.max(0, index - windowSize), Math.min(shapes.length, index + windowSize + 1));
      const bearings = neighbors.map(n => n.outBearing);

      // Calculate the average and standard deviation of bearings
      const avgBearing = bearings.reduce((sum, bearing) => sum + bearing, 0) / bearings.length;
      const stdDev = Math.sqrt(bearings.reduce((sum, bearing) => sum + Math.pow(bearing - avgBearing, 2), 0) / bearings.length);

      // Check if the current point's outBearing is within the acceptable range
      return Math.abs(shape.outBearing - avgBearing) <= threshold * stdDev;
    });
  }

  // ---------------------------------------------------------------------
  // Charge un tableau de Shape (points d'itinéraire), crée/complète des
  // véhicules, et appelle visualizeTrips() à la fin
  // ---------------------------------------------------------------------
  loadVehicles(shapes: Shape[]): void {
    // Regroupement des shapes par tripId
    const shapesByTripId = new Map<string, Shape[]>();
    const shapesCount = shapes.length;

    for (let i = 0; i < shapesCount; i++) {
      const shape = shapes[i];
      // if (i > 0 &&
      //   i < shapesCount - 1 &&
      //   ecartInterangulaire(shapes[i - 1].outBearing, shapes[i].outBearing) > 90) {
      //   shape.outBearing = angleIntermediaire(shapes[i - 1].outBearing, shapes[i + 1].outBearing);
      // }
      let tripShapes = shapesByTripId.get(shape.tripId);
      if (tripShapes === undefined) {
        tripShapes = [];
        shapesByTripId.set(shape.tripId, tripShapes);
      }
      tripShapes.push(shape);
    }
    /** Lissage des bearings */
    shapesByTripId.forEach(shapes => {
      const filtered = this.filterOutliers(shapes, 5);
      shapes.length = 0;
      shapes.push(...filtered);
    });

    // On prépare un tableau fixe pour limiter les allocations
    const BATCH_SIZE = 1000;
    const positions = new Array<VehiclePosition>(BATCH_SIZE);

    this.vehicles.clear();

    // Pour chaque tripId, on ordonne les shapes et on crée les segments
    for (const [tripId, tripShapes] of shapesByTripId) {
      // Tri par shape_pt_sequence
      tripShapes.sort((a, b) => +a.shape_pt_sequence - +b.shape_pt_sequence);

      let segments: VehiclePosition[][] = [];
      let currentSegment: VehiclePosition[] = [];
      let lastSequence = -1;
      let posIndex = 0;

      for (const shape of tripShapes) {
        const sequence = +shape.shape_pt_sequence;

        // Si on détecte un saut dans la séquence, on commence un nouveau segment
        if (lastSequence !== -1 && (sequence <= lastSequence || sequence !== lastSequence + 1)) {
          if (currentSegment.length > 0) {
            segments.push(currentSegment);
            currentSegment = [];
          }
        }

        // Réutilisation de l'emplacement posIndex
        if (posIndex === BATCH_SIZE) {
          posIndex = 0;
        }

        if (positions[posIndex] === undefined) {
          positions[posIndex] = {
            timestamp: 0,
            longitude: 0,
            latitude: 0,
            inBearing: 0,
            outBearing: 0,
            inSpeed: 0,
            outSpeed: 0,
            bearing: 0,
            shape_pt_sequence: 0,
            speed: 0
          };
        }

        // Remplit la position à réutiliser
        const position = positions[posIndex];
        position.timestamp = +shape.date;
        position.longitude = +shape.lon;
        position.latitude = +shape.lat;
        position.inBearing = +shape.inBearing;
        position.outBearing = +shape.outBearing;
        position.inSpeed = +shape.inSpeed;
        position.outSpeed = +shape.outSpeed;
        position.bearing = +shape.outBearing;
        position.shape_pt_sequence = sequence;
        position.stop_sequence = +shape.stop_sequence;

        // On place un clone dans currentSegment
        currentSegment.push({ ...position });

        lastSequence = sequence;
        posIndex++;
      }

      // Fin de boucle : push le dernier segment s'il existe
      if (currentSegment.length > 0) {
        segments.push(currentSegment);
      }

      segments = segments.filter(s => s.length > 1);

      // On complète ou on crée un Vehicle
      if (segments.length > 0) this.createNewVehicle(tripId, tripShapes[0], segments);
    }

    // ---------------------------------------------------------------------
    // À la fin, on appelle visualizeTrips() pour mettre à jour l'affichage
    // ---------------------------------------------------------------------
    // this.visualizeTrips();
  }

  // ---------------------------------------------------------------------
  // Crée un nouveau véhicule à partir du premier Shape et des segments
  // ---------------------------------------------------------------------
  private createNewVehicle(tripId: string, firstShape: Shape, segments: VehiclePosition[][]): void {
    const newVehicle = new Vehicle(
      tripId,
      this.getVehicleType(firstShape.route_type),
      firstShape.route_short_name, // On utilise route_short_name comme label
      segments
    );
    newVehicle.color = this.parseColor(firstShape.route_color);
    this.vehicles.set(tripId, newVehicle);
  }

  // ---------------------------------------------------------------------
  // Calcule les coordonnées pour dessiner une flèche directionnelle
  // ---------------------------------------------------------------------
  private calculateArrowPoints(longitude: number, latitude: number, bearing: number = 0, size: number = 0.0001): Array<[number, number]> {
    // Convertir le bearing en radians
    const bearingRad = (bearing * Math.PI) / 180;

    // Calculer le point de base de la flèche
    const basePoint: [number, number] = [longitude, latitude];

    // Calculer la pointe de la flèche
    const tipX = longitude + size * Math.sin(bearingRad);
    const tipY = latitude + size * Math.cos(bearingRad);
    const tipPoint: [number, number] = [tipX, tipY];

    // Calculer les ailes de la flèche (à 135 degrés de la pointe)
    const wingAngle1 = bearingRad + (135 * Math.PI) / 180;
    const wingAngle2 = bearingRad - (135 * Math.PI) / 180;
    const wingSize = size * 0.5; // Taille des ailes plus petite que la flèche principale

    const wing1X = tipX + wingSize * Math.sin(wingAngle1);
    const wing1Y = tipY + wingSize * Math.cos(wingAngle1);
    const wing1Point: [number, number] = [wing1X, wing1Y];

    const wing2X = tipX + wingSize * Math.sin(wingAngle2);
    const wing2Y = tipY + wingSize * Math.cos(wingAngle2);
    const wing2Point: [number, number] = [wing2X, wing2Y];

    // Retourne une ligne du point de base à la pointe, et les deux ailes
    return [basePoint, tipPoint, tipPoint, wing1Point, tipPoint, wing2Point];
  }

  // ---------------------------------------------------------------------
  // Visualise les trajets de chaque vehicle sur la carte
  //  - Efface la source/layer existante du vehicle
  //  - Crée la source + layer (line)
  //  - Crée la layer (symbol) pour l'étiquette (vehicle.label)
  //  - Ajoute des flèches pour indiquer la direction (outBearing)
  // ---------------------------------------------------------------------
  public visualizeTrips(singleVehicle?: Vehicle): void {
    if (this.map === undefined || this.map === undefined) {
      console.warn('[VehicleManager] Aucune carte définie. Appelez setMap(map).');
      return;
    }

    // Pour chaque véhicule
    for (const vehicle of this.vehicles.values()) {
      // Identifiants uniques : on ajoute "points" pour éviter collision
      const lineSourceId = `vehicle-line-source-${vehicle.id}`;
      const lineLayerId = `vehicle-line-layer-${vehicle.id}`;
      const pointSourceId = `vehicle-point-source-${vehicle.id}`;
      const pointLayerId = `vehicle-point-layer-${vehicle.id}`;
      const arrowSourceId = `vehicle-arrow-source-${vehicle.id}`;
      const arrowLayerId = `vehicle-arrow-layer-${vehicle.id}`;

      // Couleur du tracé en rgb(r,g,b)
      const lineColor = `rgb(${vehicle.color.r},${vehicle.color.g},${vehicle.color.b})`;

      // --- Nettoyer les sources et layers s'ils existent déjà
      if (this.map.getLayer(pointLayerId) != null) {
        this.map.removeLayer(pointLayerId);
      }
      if (this.map.getLayer(lineLayerId) != null) {
        this.map.removeLayer(lineLayerId);
      }
      if (this.map.getLayer(arrowLayerId) != null) {
        this.map.removeLayer(arrowLayerId);
      }
      if (this.map.getSource(pointSourceId) != null) {
        this.map.removeSource(pointSourceId);
      }
      if (this.map.getSource(lineSourceId) != null) {
        this.map.removeSource(lineSourceId);
      }
      if (this.map.getSource(arrowSourceId) != null) {
        this.map.removeSource(arrowSourceId);
      }

      if (singleVehicle !== undefined && vehicle.id !== singleVehicle.id) continue;

      // 1) On construit la geojson des segments (LineString)
      const lineGeojson = this.buildVehicleGeoJSON(vehicle);

      // 2) On construit la geojson des points (Point),
      //    où chaque point a dans "properties" le shape_pt_sequence
      const pointGeojson = this.buildVehiclePointGeoJSON(vehicle);

      // 3) On construit la geojson des flèches (MultiLineString) pour indiquer la direction
      const arrowGeojson = this.buildVehicleArrowGeoJSON(vehicle);

      // --- Créer la source "line"
      this.map.addSource(lineSourceId, {
        type: 'geojson',
        data: lineGeojson
      });

      // --- Créer la layer "line"
      this.map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: lineSourceId,
        paint: {
          'line-color': lineColor,
          'line-width': 3
        }
      });

      // --- Créer la source "points"
      this.map.addSource(pointSourceId, {
        type: 'geojson',
        data: pointGeojson
      });

      // --- Créer la source "flèches"
      this.map.addSource(arrowSourceId, {
        type: 'geojson',
        data: arrowGeojson
      });

      // --- Créer la layer "flèches" pour afficher les directions
      this.map.addLayer({
        id: arrowLayerId,
        type: 'line',
        source: arrowSourceId,
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-color': lineColor,
          'line-width': 2
        }
      });

      // --- Créer la layer "symbol" pour afficher shape_pt_sequence (actuellement désactivé)
      // this.map.addLayer({
      //   id: pointLayerId,
      //   type: 'symbol',
      //   source: pointSourceId,
      //   layout: {
      //     // On place un symbole pour chaque point
      //     'symbol-placement': 'point',
      //     // On lit la propriété "shape_pt_sequence"
      //     // pour afficher son contenu en texte
      //     'text-field': ['to-string', ['get', 'shape_pt_sequence']],
      //     'text-offset': [0, 0.6], // pour décaler un peu le texte
      //     'text-size': 12, // taille de la police
      //     'text-allow-overlap': true
      //   },
      //   paint: {
      //     'text-color': '#000000', // Couleur du texte
      //     'text-halo-color': '#FFFFFF', // Couleur de halo
      //     'text-halo-width': 2 // Epaisseur du halo
      //   }
      // });
    }
  }

  /**
   * Construit un FeatureCollection de type LineString,
   * pour les segments d'un véhicule. Pour chaque point,
   * on affiche un message de debug mentionnant le shape_pt_sequence.
   */
  private buildVehicleGeoJSON(vehicle: Vehicle): GeoJSON.FeatureCollection<GeoJSON.LineString> {
    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
      type: 'FeatureCollection',
      features: []
    };

    // Pour chaque segment, on crée un LineString
    for (let s = 0; s < vehicle.segments.length; s++) {
      const segment = vehicle.segments[s];

      // Un segment a-t-il assez de points ?
      if (segment.length < 2) {
        continue;
      }

      // On construit le tableau de coordonnées [lon, lat]
      // en loggant le shape_pt_sequence pour chaque point.
      const coordinates: Array<[number, number]> = segment.map((pos, indexInSegment) => {
        // Log de debug
        // console.debug(
        //   `[DEBUG] Vehicle=${vehicle.id}, segment=${s}, indexInSegment=${indexInSegment}, shape_pt_sequence=${pos.shape_pt_sequence}`
        // );
        // On retourne la coordonnée pour le GeoJSON
        return [pos.longitude, pos.latitude];
      });

      // Pour le tracé, on inclut quelques propriétés utiles :
      featureCollection.features.push({
        type: 'Feature',
        properties: {
          vehicleId: vehicle.id,
          vehicleLabel: vehicle.label,
          segmentIndex: s
        },
        geometry: {
          type: 'LineString',
          coordinates
        }
      });
    }

    return featureCollection;
  }

  /**
   * Construit un FeatureCollection de type "Point",
   * avec un point par position. La propriété shape_pt_sequence
   * sert à l'affichage du label.
   */
  private buildVehiclePointGeoJSON(vehicle: Vehicle): GeoJSON.FeatureCollection<GeoJSON.Point> {
    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features: []
    };

    // On parcourt chaque segment
    for (let s = 0; s < vehicle.segments.length; s++) {
      const segment = vehicle.segments[s];

      // On parcourt chaque point du segment
      for (let k = 0; k < segment.length; k++) {
        const pos = segment[k];
        // On crée un Feature de type Point
        const pointFeature: GeoJSON.Feature<GeoJSON.Point> = {
          type: 'Feature',
          properties: {
            vehicleId: vehicle.id,
            segmentIndex: s,
            shape_pt_sequence: pos.shape_pt_sequence,
            outBearing: pos.outBearing
            // on peut ajouter d'autres properties
          },
          geometry: {
            type: 'Point',
            coordinates: [pos.longitude, pos.latitude]
          }
        };

        featureCollection.features.push(pointFeature);
      }
    }

    return featureCollection;
  }

  /**
   * Construit un FeatureCollection de type "MultiLineString" pour représenter les flèches
   * indiquant la direction (outBearing) à chaque point.
   */
  private buildVehicleArrowGeoJSON(vehicle: Vehicle): GeoJSON.FeatureCollection<GeoJSON.MultiLineString> {
    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.MultiLineString> = {
      type: 'FeatureCollection',
      features: []
    };

    // Parcourir chaque segment
    for (let s = 0; s < vehicle.segments.length; s++) {
      const segment = vehicle.segments[s];

      // Parcourir chaque point du segment (on peut choisir d'afficher une flèche tous les N points)
      for (let k = 0; k < segment.length; k++) {
        // Pour éviter de surcharger la carte, on peut afficher une flèche tous les N points
        // Par exemple, tous les 3 points : if (k % 3 !== 0) continue;

        const pos = segment[k];

        // Calculer les points de la flèche pour ce point
        const arrowPoints = this.calculateArrowPoints(pos.longitude, pos.latitude, pos.outBearing);

        // Créer un Feature de type MultiLineString pour la flèche
        const arrowFeature: GeoJSON.Feature<GeoJSON.MultiLineString> = {
          type: 'Feature',
          properties: {
            vehicleId: vehicle.id,
            segmentIndex: s,
            pointIndex: k,
            shape_pt_sequence: pos.shape_pt_sequence
          },
          geometry: {
            type: 'MultiLineString',
            coordinates: [arrowPoints]
          }
        };

        featureCollection.features.push(arrowFeature);
      }
    }

    return featureCollection;
  }
}
